// Dictionary lookup for the static build — mirrors the backend /dictionary route.
// Tgdaya (Seediq) is a flat { word: "中文" } map; Bunun is { word: { zh, ... } }
// (built from the ILRDF online-dictionary PDF by scripts/build_bunun_dict.py) plus
// dict_bunun_extra.json — hand-curated function words / story-verified roots
// (sources: 齊莉莎《布農語參考語法》+ the story's own aligned Chinese lines).
// Languages never share glosses — a Bunun word must not fall back to a Seediq entry.
//
// Bunun is agglutinative, so exact match misses most inflected forms. Lookup chain:
//   extra → exact → hyphenated-compound parts → affix-stripped root (shown as 詞根).
//
// ── Why this file fetches instead of imports ─────────────────────────────────
// These 32 files are 10.1 MB, and they used to arrive as 34 static imports —
// baked into the JS bundle, parsed before the app could render, on every visit.
// Nobody ever needs more than one language's dictionary, and nobody needs any of
// it until they tap a word: a Truku reader was downloading Kavalan, Tsou, Yami
// and twelve more just to see the library list. They now come from R2 one
// dialect at a time, on first tap, cached for the session.
//
// The lookup chains below are untouched — every branch, every affix table and
// every ordering is exactly what it was. The only change is that the dictionary
// objects arrive as an argument (D) instead of a module-level import.
import { DICT_BASE, DICT_V } from './api';

// dialect -> { main, extra, acd } source files. Written out rather than derived
// from the dialect name: a derived name that is subtly wrong does not throw, it
// 404s, and a 404 here surfaces as "this word has no gloss" — indistinguishable
// from an honest miss, in a language the reader may not be able to check.
const FILES = {
  tgdaya:     { main: 'dict_tgdaya.json',     extra: 'dict_tgdaya_extra.json',      // main is flat {word: 中文}
                roots: 'dict_tgdaya_roots.json' },  // the 791 詞根 the e-dict names — see tgdayaGloss
  bunun:      { main: 'dict_bunun.json',      extra: 'dict_bunun_extra.json' },
  truku:      { main: 'dict_truku.json',      extra: 'dict_truku_extra.json',      // omnibus, 32k words
                roots: 'dict_truku_roots.json' },   // 2,194 named 詞根 — same method as tgdaya
  atayal:     { main: 'dict_atayal.json',     extra: 'dict_atayal_extra.json',
                roots: 'dict_atayal_roots.json' },  // 1,078 named 詞根 from the e-dict's root graph
  amis:       { main: 'dict_amis.json',       extra: 'dict_amis_extra.json' },
  paiwan:     { main: 'dict_paiwan.json',     extra: 'dict_paiwan_extra.json' },
  // Thao alone has a third source: Blust 2003 via ACD (lexibank/acd) — 453 forms,
  // EN gloss + morphology, filling function words the ILRDF e-dict lacks.
  thao:       { main: 'dict_thao.json',       extra: 'dict_thao_extra.json', acd: 'dict_thao_acd.json' },
  // 2016-series languages — ILRDF e-dicts, {word:{zh,en,pos}}, each with a
  // hand-curated story-verified overlay that beats it on collision.
  saisiyat:   { main: 'dict_saisiyat.json',   extra: 'dict_saisiyat_extra.json' },
  rukai:      { main: 'dict_rukai.json',      extra: 'dict_rukai_extra.json' },
  kavalan:    { main: 'dict_kavalan.json',    extra: 'dict_kavalan_extra.json' },
  tsou:       { main: 'dict_tsou.json',       extra: 'dict_tsou_extra.json' },
  puyuma:     { main: 'dict_puyuma.json',     extra: 'dict_puyuma_extra.json' },
  yami:       { main: 'dict_yami.json',       extra: 'dict_yami_extra.json' },
  sakizaya:   { main: 'dict_sakizaya.json',   extra: 'dict_sakizaya_extra.json' },
  kanakanavu: { main: 'dict_kanakanavu.json', extra: 'dict_kanakanavu_extra.json' },
  saaroa:     { main: 'dict_saaroa.json',     extra: 'dict_saaroa_extra.json' },
};

// The languages with no per-language morphology model, handled by the shared
// generic branch (exact → ʉ-folded → conservative affix strip).
const GENERIC = new Set(['saisiyat', 'rukai', 'kavalan', 'tsou', 'puyuma',
  'yami', 'sakizaya', 'kanakanavu', 'saaroa']);

// dialect -> Promise<{main, extra, acd}>. The PROMISE is cached, not the result,
// so N words tapped in quick succession share one download instead of racing N.
const loaded = new Map();

async function fetchJson(file) {
  const r = await fetch(`${DICT_BASE}/${file}${DICT_V}`);
  if (!r.ok) throw new Error(`${file}: HTTP ${r.status}`);
  return r.json();
}

function loadDialect(dialect) {
  const spec = FILES[dialect];
  if (!spec) return Promise.resolve(null);
  let p = loaded.get(dialect);
  if (!p) {
    p = Promise.all([
      fetchJson(spec.main),
      spec.extra ? fetchJson(spec.extra) : Promise.resolve({}),
      spec.acd ? fetchJson(spec.acd) : Promise.resolve({}),
      // A missing whitelist must not mute a language: an empty Set turns the
      // affix chain off and leaves exact + bare() working exactly as before.
      spec.roots ? fetchJson(spec.roots).catch(() => []) : Promise.resolve([]),
    ]).then(([main, extra, acd, roots]) => {
      // The overlay is keyed the way its author wrote the word; the book may
      // print the same word without its glottal letter (extra 'ayaw, story
      // ayaw; extra o'syuw, story osyuw). hit() already forgives this for the
      // main dictionary, so give the overlay the same bare alias — only where
      // no bare-spelled entry exists, so a deliberate pair stays distinct.
      for (const k of Object.keys(extra)) {
        const b = bare(k);
        if (b !== k && !(b in extra)) extra[b] = extra[k];
      }
      return { main, extra, acd, roots: new Set(roots) };
    });
    // Do not let one dropped connection mute a language for the rest of the
    // session: drop the rejected promise so the next tap retries. Without this
    // the cache would faithfully replay the failure forever.
    p.catch(() => loaded.delete(dialect));
    loaded.set(dialect, p);
  }
  return p;
}

// Conservative pan-Austronesian affix stripper for the generic langs (they have
// no per-language morphology model like Bunun/Thao). Strips only the most
// universal AV/aspect prefixes, the -in-/-um- infixes and CV(C) reduplication,
// and accepts a candidate ONLY if it lands on a real dict entry (len>=3) — so a
// wrong strip surfaces nothing rather than a fabricated gloss. Result is labeled
// 詞根 so it never masquerades as an exact gloss.
const GEN_PREF = ['mapa', 'masi', 'maka', 'mata', 'pina', 'pini', 'muru', 'mu', 'ma', 'mi',
  'ni', 'pa', 'pi', 'pu', 'ka', 'ki', 'ku', 'si', 'sa', 'ta', 'ti', 'tu', 'na', 'a', 'i', 'm'];
const GEN_SUF = ['ana', 'ane', 'ani', 'aan', 'anʉ', 'an', 'en', 'ʉn', 'un', 'in', 'ay', 'aw', 'a', 'i', 'u'];
// Yami/Tao adds micya- (maka- is already in GEN_PREF) — an actor-voice prefix
// the shared 'mi' entry can't reach, since the BFS only strips whole listed
// prefixes and 'cya' isn't one, so 'mi'+'cyavehan' never re-strips to the root.
const YAMI_PREF = ['micya'];
// Root candidates are checked against extra as well as main: extra is a
// curated, verified overlay (not a guess), and words like Yami micyalatolatos
// (億, story population count) strip cleanly to a root that only extra has —
// excluding it there meant a mechanically-correct strip still surfaced nothing.
function genRoots(D, key, extraPref = []) {
  const seen = new Set([key]);
  let frontier = [key];
  const hits = [];
  const prefixes = extraPref.length ? [...extraPref, ...GEN_PREF] : GEN_PREF;
  for (let depth = 0; depth < 3 && frontier.length; depth++) {
    const next = [];
    for (const x of frontier) {
      const cands = [];
      for (const p of prefixes) if (x.startsWith(p) && x.length - p.length >= 3) cands.push(x.slice(p.length));
      for (const s of GEN_SUF) if (x.endsWith(s) && x.length - s.length >= 3) cands.push(x.slice(0, x.length - s.length));
      if (x.length > 4 && (x.slice(1, 3) === 'in' || x.slice(1, 3) === 'um')) cands.push(x[0] + x.slice(3));
      if (x.length > 5 && x.slice(0, 2) === x.slice(2, 4)) cands.push(x.slice(2)); // CV redup
      if (x.length > 6 && x.slice(0, 3) === x.slice(3, 6)) cands.push(x.slice(3)); // CVC redup
      for (const c of cands) {
        if (seen.has(c)) continue;
        seen.add(c); next.push(c);
        // >=5: short roots collide with unrelated entries (wrong gloss worse than none)
        if (c.length >= 5 && (hit(D.main, c) || extraHit(D, c))) hits.push(c);
      }
    }
    frontier = next;
  }
  return hits.sort((a, b) => b.length - a.length);
}

// Match the backend. Apostrophes (’ in story text, ʼ in the ILRDF dicts) and
// Amis ^ are LETTERS (glottal/epiglottal stops), not punctuation — they unify
// to ASCII ' and stay in the word. Only true punctuation is stripped.
const norm = (w) => (w || '').toLowerCase()
  .replace(/[ʼ’‘]/g, "'")
  .replace(/[^\w'^\- ]/g, '')
  .trim();

// Exact key first (apostrophes/^ are letters and can distinguish words), then
// the bare form — story orthography and the dictionary don't always agree on
// writing glottal letters (dict naʼ vs story na, story ’oriraw vs dict oriraw).
const bare = (k) => k.replace(/['^]/g, '');
const hit = (d, k) => (d[k] && d[k].zh) || (d[bare(k)] && d[bare(k)].zh) || '';
// extra dicts are flat {word: "zh string"} (no .zh wrapper), so they need their
// own accessor rather than hit()'s d[k].zh shape.
const extraHit = (D, k) => (D.extra && (D.extra[k] || D.extra[bare(k)])) || '';

// Extra beats the PDF-derived dict (same rule as every other language) so
// hand-curated overrides can correct wrong-sense e-dict entries (lipuah, suhis).
const bununEntry = (D, k) => D.extra[k] || D.extra[bare(k)] || hit(D.main, k) || '';

// Affix layers seen in the grammar (齊莉莎) + the ILRDF dictionary's 構詞 fields.
const PREF = ['makan', 'mapa', 'masi', 'maka', 'malas', 'malan', 'mali', 'mai', 'tau', 'taus',
  'pin', 'pun', 'pis', 'pas', 'pai', 'kan', 'min', 'mun', 'nai', 'sin', 'tis',
  'ma', 'pa', 'pi', 'ka', 'ki', 'mu', 'na', 'in', 'si', 'sa', 'i', 'un', 'u', 'a'];
const SUF = ['un', 'an', 'in', 'ik', 'az', 'ang', 'a', 'i', 'cia', 'tia', 'tin', 'ti',
  'ku', 'ta', 'am', 'av', 'nis', 'nta', 'tan', 's', 'v'];

// BFS over affix strips (≤4 layers); return dictionary roots, longest first.
function bununRoots(D, key) {
  const seen = new Set([key]);
  let frontier = [key];
  const hits = [];
  for (let depth = 0; depth < 4 && frontier.length; depth++) {
    const next = [];
    for (const x of frontier) {
      const cands = [];
      for (const p of PREF) if (x.startsWith(p) && x.length - p.length >= 3) cands.push(x.slice(p.length));
      for (const s of SUF) if (x.endsWith(s) && x.length - s.length >= 3) cands.push(x.slice(0, x.length - s.length));
      if (x.length > 4 && x.slice(1, 3) === 'in') cands.push(x[0] + x.slice(3));   // -in- infix
      if (x.length > 5 && x.slice(0, 2) === x.slice(2, 4)) cands.push(x.slice(2)); // CV reduplication
      if (x.length > 5 && x.slice(0, 3) === x.slice(3, 6)) cands.push(x.slice(3)); // CVC reduplication
      for (const c of cands) {
        if (seen.has(c)) continue;
        seen.add(c); next.push(c);
        if (c.length >= 3 && bununEntry(D, c)) hits.push(c);
      }
    }
    frontier = next;
  }
  return hits.sort((a, b) => b.length - a.length);
}

function bununGloss(D, key, depth = 0) {
  const direct = bununEntry(D, key);
  if (direct) return direct;
  if (key.includes('-') && depth < 2) {            // compound: gloss each part
    const parts = key.split('-').filter(Boolean).map(p => bununGloss(D, p, depth + 1));
    if (parts.length && parts.every(Boolean)) return parts.join('＋');
  }
  const root = bununRoots(D, key)[0];
  if (root) return `詞根 ${root}：${bununEntry(D, root)}`;
  return '';
}

// Thao is agglutinative and the e-dict stores citation roots (its 構詞 field is
// empty for every entry), so bare exact match misses inflected story forms.
// Strip the common AV/dynamic prefixes (m-/ma-/mi-/mu-/min-/mun-/pa-/pia-/pin-),
// the -in-/-um- infixes and CV(C) reduplication down to a dictionary root, and
// also try adding an m- prefix (story bare root vs dict m-form). Roots are shown
// labeled 詞根 so a stripped guess never masquerades as the exact gloss.
const thaoEntry = (D, k) => hit(D.main, k);
const THAO_PREF = ['minu', 'mun', 'min', 'pia', 'pin', 'ma', 'mi', 'mu', 'pa', 'sh', 'qm', 'km', 'ka', 'qa', 'm'];
function thaoRoots(D, key) {
  const seen = new Set([key]);
  let frontier = [key];
  const hits = [];
  for (let depth = 0; depth < 4 && frontier.length; depth++) {
    const next = [];
    for (const x of frontier) {
      const cands = [];
      for (const p of THAO_PREF) if (x.startsWith(p) && x.length - p.length >= 3) cands.push(x.slice(p.length));
      if (x.length > 4 && (x.slice(1, 3) === 'in' || x.slice(1, 3) === 'um')) cands.push(x[0] + x.slice(3)); // -in-/-um-
      if (x.length > 5 && x.slice(0, 2) === x.slice(2, 4)) cands.push(x.slice(2)); // CV redup
      if (x.length > 6 && x.slice(0, 3) === x.slice(3, 6)) cands.push(x.slice(3)); // CVC redup
      for (const c of cands) {
        if (seen.has(c)) continue;
        seen.add(c); next.push(c);
        if (c.length >= 3 && thaoEntry(D, c)) hits.push(c);
      }
    }
    frontier = next;
  }
  return hits.sort((a, b) => b.length - a.length);
}
const thaoAcdEntry = (D, k) => D.acd[k] || D.acd[bare(k)] || '';
function thaoGloss(D, key, retried) {
  // An empty-string overlay is an intentional blank: neither Thao dictionary
  // attests the word, and the fallback chain would surface a bogus hit
  // (shpuza) — show nothing rather than mislead.
  const ex = key in D.extra ? D.extra[key] : D.extra[bare(key)];
  if (ex !== undefined) return ex;
  const direct = thaoEntry(D, key);
  if (direct) return direct;
  for (const m of ['ma', 'mi', 'mu', 'min', 'mun', 'm']) {   // story bare root → dict m-form
    const g = thaoEntry(D, m + key);
    if (g) return `參 ${m}${key}：${g}`;
  }
  // Blust/ACD exact match (English) — a verified lexicon entry beats a stripped
  // e-dict guess, so try it before the affix-stripped root fallback.
  const acd = thaoAcdEntry(D, key);
  if (acd) return `〔Blust〕${acd}`;
  const root = thaoRoots(D, key)[0];
  if (root) return `詞根 ${root}：${thaoEntry(D, root)}`;
  // The story transcript follows Blust in writing offglides as -y (izay, day,
  // munay) where the ILRDF e-dict writes -i (izai, dai, munai). Retry the whole
  // chain over each vowel+y → vowel+i substitution (independently, not globally
  // — thaythuy needs only its first y folded to hit dict thaithuy). Guarded by
  // `retried` so a variant can't fold again; only fires after everything above
  // missed, so it can't shadow a real -y entry.
  if (!retried) {
    const spots = [];
    for (let i = 0; i + 1 < key.length && spots.length < 4; i++) {
      if ('aeiou'.includes(key[i]) && key[i + 1] === 'y') spots.push(i + 1);
    }
    for (let mask = 1; mask < (1 << spots.length); mask++) {
      const chars = key.split('');
      spots.forEach((pos, j) => { if (mask & (1 << j)) chars[pos] = 'i'; });
      const variant = chars.join('');
      const g = thaoGloss(D, variant, true);
      if (g) return g.startsWith('參 ') || g.startsWith('詞根 ') || g.startsWith('〔') ? g : `參 ${variant}：${g}`;
    }
  }
  return '';
}

// Tgdaya had exact + bare() and nothing else, so every prefixed or infixed form
// missed even when its root sat in the dictionary — the single largest slice of
// its residue. The affix tables below are not hand-written: they are the affixes
// that appear 15+ times when each of the 5,695 (derived form, 詞根) pairs in the
// ILRDF 依詞根、衍生詞排序 export is aligned against its stated root
// (scripts/build_tgdaya_roots.py carries the derivation).
//
// Stripping with them freely resolves the wrong root 36.5% of the time — 寧可從缺
// forbids shipping that. What makes it safe is the whitelist: a strip is accepted
// only when what is left is a string the dictionary ITSELF names as a 詞根, which
// takes precision to 92.2% measured against those same pairs as gold. Hence a
// data file rather than a longer affix table — the constraint is lexical, and no
// affix list can encode it.
//
// The result is labeled 參 詞根, never returned as the word's own gloss: it says
// the reader is looking at a derivative of a word the dictionary does hold.
// The tables are printed by the builders (scripts/build_tgdaya_roots.py,
// scripts/build_truku_roots.py) from the affixes that occur often enough in
// their own export's (form, 詞根) pairs — 15+ for tgdaya, 25+ for truku, which
// is the largest table whose measured precision still clears 95%. Longest
// first, so 'mkm' is tried before 'm'.
const TG_PREF = [
  'mkm', 'km', 'kn', 'mg', 'mn', 'mp', 'ms', 'mt', 'pk', 'pn', 'ps', 'sm', 'sn', 'tg',
  'tm', 'tn', 'k', 'm', 'n', 'p', 'q', 's', 't'];
const TG_INF = ['mn', 'n', 'm'];   // after the first consonant: bnrahan < brahan
// Suffixes are NOT derived the same way: -un/-an are the productive voice
// endings and the exports state them far less often than they state prefixes,
// so these stay the conservative lists. The whitelist guards them either way.
const TG_SUF = ['an', 'un', 'ay', 'u', 'i', 'e', 'n'];
// Truku's export is eight times the size of Seediq's (70,569 headwords against
// 8,224; 52,650 root-tagged), which is why its table is so much longer — and
// why its measured precision is HIGHER, not lower: 95.1% against 92.2%.
const TK_PREF = [
  'dmpte', 'empaa', 'empee', 'empte', 'mkmpe', 'mkmps', 'mnege', 'mnegs', 'mnegt', 'pnege', 'pnegs', 'dmpe', 'dmpg', 'dmps',
  'dmpt', 'empe', 'empg', 'empk', 'emps', 'empt', 'gmne', 'kmne', 'mkme', 'mkmp', 'mkms', 'mneg', 'mqmp', 'msne',
  'mtge', 'pneg', 'ptge', 'skne', 'smee', 'sneg', 'sqne', 'tmee', 'tmne', 'dme', 'dmp', 'dmt', 'emb', 'emp',
  'gme', 'gmn', 'gne', 'kke', 'kks', 'kme', 'kmn', 'kms', 'kne', 'kns', 'maa', 'mge', 'mkk', 'mkm',
  'mkn', 'mme', 'mne', 'mns', 'mnt', 'mqm', 'msn', 'mtg', 'nke', 'nps', 'paa', 'pge', 'pke', 'pne',
  'pnk', 'pns', 'pnt', 'ppe', 'pps', 'ptg', 'qmn', 'sge', 'ske', 'skn', 'smb', 'smd', 'sme', 'smg',
  'smh', 'smn', 'smt', 'spe', 'spg', 'sps', 'sqn', 'tee', 'tge', 'tmb', 'tme', 'tmg', 'tmh', 'tmn',
  'tne', 'tte', 'dd', 'de', 'dm', 'em', 'en', 'ge', 'gg', 'gm', 'gn', 'ke', 'kk', 'km',
  'kn', 'md', 'me', 'mg', 'mh', 'mk', 'mm', 'mn', 'mq', 'mr', 'ms', 'mt', 'ne', 'nk',
  'np', 'nq', 'ns', 'pd', 'pe', 'pg', 'ph', 'pk', 'pm', 'pn', 'pp', 'ps', 'pt', 'qm',
  'qn', 'qq', 'sb', 'se', 'sg', 'sk', 'sm', 'sn', 'sp', 'sq', 'ss', 'st', 'tb', 'td',
  'te', 'tg', 'th', 'tk', 'tm', 'tn', 'tt', 'b', 'd', 'e', 'g', 'h', 'k', 'm',
  'n', 'p', 'q', 's', 't'];
const TK_INF = ['mn', 'n', 'm'];
const TK_SUF = ['ani', 'an', 'aw', 'ay', 'un', 'a', 'i'];
// Atayal's pairs come from the e-dictionary's own root-graph API rather than
// a PDF (scripts/crawl_atayal_roots.py): 3,803 (form, 詞根) pairs, 12+ is the
// largest table that clears 95% (96.2%); every dictionary-wide second tier
// measured 74-93%, so the whitelist stays the only licence.
const AT_PREF = ['min', 'cin', 'kin', 'm', 'p', 'q', 's', 't', 'b', 'k'];
const AT_INF = ['in', 'n', 'm'];
const AT_SUF = ['an', 'ay', 'un', 'aw', 'i'];
const TG_MIN = 3;                        // a 2-char remainder is a particle, not a root
const seediqEntry = (D, k) => (D.extra && (D.extra[k] || D.extra[bare(k)])) ||
  (D.main[k] && (D.main[k].zh || D.main[k])) || (D.main[bare(k)] && (D.main[bare(k)].zh || D.main[bare(k)])) || '';
// Shared by tgdaya, truku and atayal: same family, same kind of export, same
// constraint — only the tables differ, and all are derived rather than written.
function namedRoot(D, key, PRE, INF, SUF) {
  const named = (r) => D.roots && D.roots.has(r) && seediqEntry(D, r);
  for (const pre of PRE) {
    if (key.startsWith(pre) && key.length - pre.length >= TG_MIN) {
      const r = key.slice(pre.length), g = named(r);
      if (g) return { root: r, gloss: g };
    }
  }
  for (const inf of INF) {
    if (key.length > inf.length + TG_MIN && key.slice(1, 1 + inf.length) === inf) {
      const r = key[0] + key.slice(1 + inf.length), g = named(r);
      if (g) return { root: r, gloss: g };
    }
  }
  for (const suf of SUF) {
    if (key.endsWith(suf) && key.length - suf.length >= TG_MIN) {
      const r = key.slice(0, key.length - suf.length), g = named(r);
      if (g) return { root: r, gloss: g };
    }
  }
  return null;
}

// Async because the dictionary is fetched on first use — see the note at the top.
// A failed fetch resolves to '' (no gloss) rather than rejecting: a missing
// dictionary should look like a word the dictionary does not cover, not like a
// broken popup. loadDialect has already dropped the failure so the next tap retries.
async function lookupOnce(word, dialect) {
  const key = norm(word);
  if (!key) return '';
  let D;
  try {
    D = await loadDialect(dialect);
  } catch {
    return '';
  }
  if (!D) return '';
  if (dialect === 'bunun') return bununGloss(D, key);
  // Truku had extra || exact and nothing else — the same hole tgdaya had, and
  // the same fix: its own export names 2,194 roots, so a strip onto one of them
  // is a cross-reference the dictionary itself licenses (95.1% precision).
  if (dialect === 'truku') {
    const direct = D.extra[key] || D.extra[bare(key)] || hit(D.main, key) || hit(D.main, bare(key));
    if (direct) return direct;
    const r = namedRoot(D, key, TK_PREF, TK_INF, TK_SUF);
    if (r) return `參 詞根 ${r.root}：${String(r.gloss).split('\n')[0]}`;
    return '';
  }
  // Amis extras were exact-only: the overlay writes bare keys (tanuulah) while
  // the book prints the glottal (Tanu’ulah), so the story form missed its own
  // curated entry. Same extra-then-bare rule as every other language.
  if (dialect === 'amis') return D.extra[key] || D.extra[bare(key)] || hit(D.main, key);
  if (dialect === 'atayal') {
    // curated senses first (the ILRDF dict files 六 under mtzyuʼ and gives bare
    // tzyuʼ only 方布 — homographs need story-verified overrides), then the
    // dictionary, then a labeled cross-reference to the m- form / bare root.
    if (D.extra[key]) return D.extra[key];
    const direct = hit(D.main, key);
    if (direct) return direct;
    const m = D.main['m' + key];
    if (m && m.zh) return `參 m${key}：${m.zh}`;
    if (key.startsWith('m')) {
      const b = D.main[key.slice(1)] || D.main[bare(key.slice(1))];
      if (b && b.zh) return `參 詞根 ${key.slice(1)}：${b.zh}`;
    }
    // then any other affix, gated by the roots the e-dict itself names
    const r = namedRoot(D, key, AT_PREF, AT_INF, AT_SUF);
    if (r) return `參 詞根 ${r.root}：${String(r.gloss).split('\n')[0]}`;
    return '';
  }
  if (dialect === 'paiwan') return D.extra[key] || D.extra[bare(key)] || hit(D.main, key);
  if (dialect === 'thao') return thaoGloss(D, key);
  // tgdaya's main is the one flat {word: 中文} file, so it has no sense pile to
  // fold and never needed an overlay — until the annotations. The overlay is
  // where the 構詞/用法 lines live, so it has to win here exactly as it does
  // everywhere else, or a tgdaya reader gets the bare gloss and no button.
  if (dialect === 'tgdaya') {
    const ex = D.extra;
    if (ex && (ex[key] || ex[bare(key)])) return ex[key] || ex[bare(key)];
    const direct = D.main[key] || D.main[bare(key)];
    if (direct) return direct;
    const r = namedRoot(D, key, TG_PREF, TG_INF, TG_SUF);
    // The gloss can carry the overlay's 構詞/用法 lines; keep only its first line
    // here, or a cross-reference would quote another word's usage notes as if
    // they were this one's.
    if (r) return `參 詞根 ${r.root}：${String(r.gloss).split('\n')[0]}`;
    return '';
  }
  if (GENERIC.has(dialect)) {
    const d = D.main;
    const ex = D.extra;
    if (ex && (ex[key] || ex[bare(key)])) return ex[key] || ex[bare(key)];
    // The ILRDF e-dicts dropped the barred-i vowel ʉ from every headword
    // (tsou/kanakanavu/saaroa each store 0 keys with ʉ) while the story
    // orthography keeps it, so ʉ-forms miss on exact match. Fold ʉ out as a
    // fallback: exact key first, then the ʉ-folded key — but only when the fold
    // is still >=4 chars, since 2-3 char folds collide with unrelated short
    // particles/numerals (e.g. saaroa hlʉ→hl 骨髓, tsou alʉ→al) — 寧可從缺.
    // genRoots runs on the folded key too; its own >=5 guard keeps roots safe.
    const fkey = key.replace(/ʉ/g, '');
    let direct = hit(d, key);
    if (!direct && fkey !== key && fkey.length >= 4) direct = hit(d, fkey);
    if (direct) return direct;
    const root = genRoots(D, fkey, dialect === 'yami' ? YAMI_PREF : [])[0];
    if (root) return `詞根 ${root}：${hit(d, root) || extraHit(D, root)}`;
    // Saaroa only: the printed book's own orthography glues clitic strings and
    // compounds (ʉnʉmʉcaihli = ʉnʉmʉ 六 + caihli 年; tumapapiahlicu+aku), so the
    // transcript stays book-faithful and lookup does the splitting. Fires only
    // after everything above missed; both parts must resolve through the same
    // chain (direct beats 詞根-guess candidates; one part must be ≥5 chars so
    // two short spurious senses can't pair up). The result shows both parts with
    // their glosses so it never reads as a single dictionary entry, and the
    // extra overlay — checked before all of this — can veto or override any
    // split that picks a wrong sense (寧可從缺 still applies: no resolve, no gloss).
    if (dialect === 'saaroa') {
      // Glosses stay untrimmed: picking "the first sense" chose 下巴 over 但是
      // for ahla and cut kapitan's note mid-parenthesis — sense selection is the
      // reader's judgment, same as a direct multi-sense hit.
      const resolve = (part) => {
        const direct = extraHit(D, part) || hit(d, part);
        if (direct) return { g: direct, direct: true };
        const r = genRoots(D, part)[0];
        if (r) return { g: `詞根 ${r}：${hit(d, r) || extraHit(D, r)}`, direct: false };
        return null;
      };
      let best = null;
      for (let i = 3; i <= key.length - 3; i++) {
        const a = key.slice(0, i), b = key.slice(i);
        if (a.length < 5 && b.length < 5) continue;
        const ra = resolve(a), rb = resolve(b);
        if (!ra || !rb) continue;
        const score = (ra.direct + rb.direct) * 100 + Math.min(a.length, b.length);
        if (!best || score > best.score) best = { score, a, b, ra, rb };
      }
      if (best) return `${best.a}「${best.ra.g}」＋${best.b}「${best.rb.g}」`;
    }
    return '';
  }
  return '';
}


// Two of the 105年 會說話的葉子 readings are printed in an orthography their
// dictionary does not use. It is a spelling difference, not a lexical one: Amis
// writes the /u/ vowel `u` where the dictionary writes `o` — the numerals settle
// it (tusa/tosa 二, pitu/pito 七, tulu/tolo 三), as do the four case markers
// nu/no ku/ko tu/to u/o, which alone are 771 of the book's tokens. Puyuma writes
// the retroflex `z` where the dictionary writes `dr` (kuzulr/kudrulr 千,
// zuwa/druwa 二). Respelling recovers 996 Amis tokens over 76 word types and 225
// Puyuma tokens over 50, and no two source spellings land on one headword, so it
// adds no ambiguity the reader would have to arbitrate.
//
//
// The 葉子 editions add one letter each, measured the same way. Amis writes `b`
// where the dictionary writes `f` (binawlan/finawlan 部落, mabana'/mafana' 知道,
// cabay/cafay 同伴): 271 of the book's tokens carry b and 5 carry f, against one
// headword with b and 1,615 with f. Puyuma writes `v` for `b` (vavayan/babayan
// 女性, vati/bati 話): 159 tokens against 14, and no headword has v at all. The
// respelled glosses match their sentences' 中文 at least as often as exact hits
// do (35%/31% against 25%). Kavalan q/k was tried and refused — both letters
// live on both sides. The substitutions compose into one alternative: a
// headword the dictionary never spells with u or b cannot be lost by dropping
// both.
//
// It runs LAST, only after the whole chain has missed, so it can never overrule
// an attested reading — it can only turn a blank into the dictionary's own gloss
// for the same word. A respelling that finds nothing stays blank (寧可從缺).
const VARIANT = {
  amis: [[/u/g, 'o'], [/b/g, 'f']],
  puyuma: [[/z/g, 'dr'], [/v/g, 'b']],
};

export async function lookupGloss(word, dialect) {
  const g = await lookupOnce(word, dialect);
  if (g) return g;
  const v = VARIANT[dialect];
  if (!v) return '';
  // On the lowercased word: a sentence-initial U or Binawlan is the same word,
  // and /u/g does not see a capital — 27 of the Amis book's 90 bare `u` missed
  // this way before the sweep caught it.
  const low = (word || '').toLowerCase();
  const alt = v.reduce((w, [from, to]) => w.replace(from, to), low);
  return alt && alt !== low ? await lookupOnce(alt, dialect) : '';
}

// The curated *_extra overlay WINS in lookupGloss — that is the whole point of
// it — which means the e-dict's own entry for the same headword becomes
// invisible. For most words that is right (the overlay exists because the pile
// was unreadable), but the overlay is a judgement call and the dictionary is the
// evidence for it, so the popup shows it one tap down. Exact headword only: no
// overlay, no affix stripping, no compound splitting — anything derived is a
// guess of ours, not something the book prints under this word, and a guess
// carrying the label 詞典 would be a lie about its own provenance.
export async function lookupMainGloss(word, dialect) {
  const key = norm(word);
  if (!key) return '';
  let D;
  try {
    D = await loadDialect(dialect);
  } catch {
    return '';
  }
  if (!D || !D.main) return '';
  // tgdaya's file is flat {word: 中文}; every other main is {word: {zh}}.
  const e = D.main[key] || D.main[bare(key)];
  if (!e) return '';
  return typeof e === 'string' ? e : (e.zh || '');
}
